import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Loader2, TrendingUp, Users, Clock, DollarSign, Sparkles, ShieldCheck,
  User as UserIcon, ArrowUpCircle, RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TemplateAnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promotingId, setPromotingId] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/planner/templates/analytics`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const promote = async (id, name) => {
    if (!window.confirm(`Promote "${name}" to a curated system template? It will be visible to all users.`)) return;
    setPromotingId(id);
    try {
      await axios.post(`${API_URL}/api/planner/templates/${id}/promote`, {}, { withCredentials: true });
      toast.success(`Promoted "${name}" to system library`);
      fetchAnalytics();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to promote');
    } finally {
      setPromotingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="template-analytics-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#0F2040]" />
      </div>
    );
  }

  if (!data) return null;

  const sortedTemplates = [...data.templates].sort((a, b) => (b.apply_count || 0) - (a.apply_count || 0));

  return (
    <div className="space-y-5" data-testid="template-analytics-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-[#0F2040]">Template Analytics</h2>
          <p className="text-xs text-slate-500 mt-0.5">Usage signal across the Planner Template Library.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchAnalytics}
          className="text-[10px] text-slate-500 h-7 px-2" data-testid="analytics-refresh-btn">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard icon={TrendingUp} color="text-[#0F2040]" label="Total applies" value={data.total_applies} />
        <StatCard icon={Sparkles} color="text-[#4ABFCF]" label="Templates" value={data.total_templates} />
        <StatCard icon={ArrowUpCircle} color="text-amber-500" label="Promotion candidates" value={data.promotion_candidates?.length || 0} />
      </div>

      {/* Promotion candidates */}
      {data.promotion_candidates && data.promotion_candidates.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="promotion-candidates-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
              <ArrowUpCircle className="w-4 h-4 text-amber-500" /> Promotion Candidates
            </CardTitle>
            <p className="text-[11px] text-slate-500">Personal templates applied 3+ times — candidates to curate for the whole org.</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.promotion_candidates.map(t => (
              <div key={t.id}
                className="flex items-center justify-between bg-white rounded-md border border-amber-200 px-3 py-2"
                data-testid={`promotion-candidate-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#0F2040] truncate">{t.name}</div>
                  <div className="text-[10px] text-slate-500">
                    By {t.owner_id} · {t.apply_count} applies · {t.unique_user_count} user(s)
                  </div>
                </div>
                <Button size="sm" onClick={() => promote(t.id, t.name)} disabled={promotingId === t.id}
                  className="bg-amber-500 hover:bg-amber-600 text-white text-[10px] h-7 px-2.5"
                  data-testid={`promote-btn-${t.id}`}>
                  {promotingId === t.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ArrowUpCircle className="w-3 h-3 mr-1" />}
                  Promote
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Full table */}
      <Card data-testid="template-analytics-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-[#0F2040]">All Templates</CardTitle>
        </CardHeader>
        <CardContent>
          {sortedTemplates.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-3">No templates found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#0F2040] text-white">
                    <th className="text-left px-3 py-2 rounded-tl-md">Name</th>
                    <th className="text-left px-3 py-2">Scope</th>
                    <th className="text-right px-3 py-2">Applies</th>
                    <th className="text-right px-3 py-2">Users</th>
                    <th className="text-right px-3 py-2">Avg Effort</th>
                    <th className="text-right px-3 py-2">Avg Cost</th>
                    <th className="text-left px-3 py-2 rounded-tr-md">Last applied</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTemplates.map((t, i) => (
                    <tr key={t.id} className={i % 2 ? 'bg-slate-50' : ''} data-testid={`analytics-row-${t.id}`}>
                      <td className="px-3 py-2 font-medium text-[#0F2040]">
                        {t.name}
                        {t.is_seed && <Badge variant="secondary" className="ml-1.5 text-[9px] h-4 px-1.5 bg-[#4ABFCF]/10 text-[#4ABFCF] border-[#4ABFCF]/20">Built-in</Badge>}
                        {t.promoted_from_id && <Badge variant="secondary" className="ml-1.5 text-[9px] h-4 px-1.5 bg-amber-50 text-amber-700 border-amber-200">Promoted</Badge>}
                      </td>
                      <td className="px-3 py-2">
                        {t.scope === 'system'
                          ? <span className="inline-flex items-center gap-1 text-[10px] text-[#4ABFCF]"><ShieldCheck className="w-3 h-3" /> System</span>
                          : <span className="inline-flex items-center gap-1 text-[10px] text-slate-500"><UserIcon className="w-3 h-3" /> {t.owner_id}</span>
                        }
                      </td>
                      <td className="text-right px-3 py-2 font-semibold">{t.apply_count}</td>
                      <td className="text-right px-3 py-2">{t.unique_user_count}</td>
                      <td className="text-right px-3 py-2">{t.avg_effort ? `${t.avg_effort} hrs` : '—'}</td>
                      <td className="text-right px-3 py-2">{t.avg_cost ? `$${t.avg_cost.toLocaleString()}` : '—'}</td>
                      <td className="px-3 py-2 text-slate-500 text-[11px]">
                        {t.last_applied ? new Date(t.last_applied).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card data-testid={`analytics-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <Icon className={`w-6 h-6 ${color}`} />
        <div>
          <p className="text-xl font-bold text-[#0F2040]">{value}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
